<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { format, fromUnixTime } from 'date-fns';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const conversations = ref([]);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const {
      data: {
        data: { payload },
      },
    } = await ConversationApi.get({ assigneeType: 'me', status: 'all' });
    conversations.value = payload;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchData();
});

const inboxName = inboxId =>
  inboxes.value.find(inbox => inbox.id === inboxId)?.name || '—';

const sortedConversations = computed(() =>
  [...conversations.value].sort(
    (a, b) => b.last_activity_at - a.last_activity_at
  )
);

const openConversation = conversationId => {
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.MEU_ESPACO.SECTIONS.CONVERSATIONS') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.MEU_ESPACO.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.MEU_ESPACO.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.MEU_ESPACO.LOADING') }}
    </p>
    <p
      v-else-if="!sortedConversations.length"
      class="px-3 pb-3 text-sm text-n-slate-10"
    >
      {{ t('DASHBOARD.MEU_ESPACO.EMPTY_STATE') }}
    </p>
    <div v-else class="px-3 pb-3 overflow-y-auto overflow-x-auto max-h-80">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-10">
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.CONVERSATIONS.CONTACT') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.CONVERSATIONS.CHANNEL') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.CONVERSATIONS.STATUS') }}
            </th>
            <th class="py-1 pr-3">
              {{ t('DASHBOARD.MEU_ESPACO.CONVERSATIONS.LAST_ACTIVITY') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="conversation in sortedConversations"
            :key="conversation.id"
            class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
            @click="openConversation(conversation.id)"
          >
            <td class="py-1 pr-3 text-n-slate-12">
              {{
                conversation.meta.sender?.name ||
                t('DASHBOARD.MEU_ESPACO.CONVERSATIONS.NO_CONTACT')
              }}
            </td>
            <td class="py-1 pr-3">{{ inboxName(conversation.inbox_id) }}</td>
            <td class="py-1 pr-3">{{ conversation.status }}</td>
            <td class="py-1 pr-3">
              {{
                format(
                  fromUnixTime(conversation.last_activity_at),
                  'dd/MM/yyyy HH:mm'
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
